<template>
    <v-container fluid class="pb-0">
        <v-row no-gutters cols="12" class="pb-2">
            <v-col cols="2" v-if="enableWrite">
                <v-btn-primary
                    data-cy="accountingWorkspace-add"
                    block
                    @click="dialogCreateAccountingWorkspace = !dialogCreateAccountingWorkspace"
                >
                    Ajouter
                </v-btn-primary>
            </v-col>
            <v-col class="pl-2">
                <v-text-field
                    v-model="search"
                    label="Recherche"
                    append-inner-icon="$search"
                    clearable
                    @click:clear="search = ''"
                    outlined
                    hide-details
                    density="compact"
                    data-cy="accountingWorkspace-searchGroup"
                />
            </v-col>
            <v-col cols="1" v-if="!$vuetify.display.mobile">
                <TutorialButtonDialog :starterComponents="'AccountingWorkspaces;'">
                </TutorialButtonDialog>
            </v-col>
        </v-row>
        <div
            v-if="accountingWorkspaces.length"
            class="d-flex justify-center"
            :style="'max-height:' + maxHeight + ';' + 'overflow:auto'"
        >
            <v-virtual-scroll
                :cols="$vuetify.display.mobile ? '12' : '8'"
                class="my-0 pa-2"
                :items="sortedAccountingWorkspaces"
            >
                <template v-slot="{ item: accWorkspace }">
                    <v-card class="default_card my-2" elevation="0" pa-0 style="margin: 5px">
                        <v-row class="d-flex align-center">
                            <v-col
                                cols="2"
                                v-if="!$vuetify.display.mobile"
                                @click="editAccountingWorkspace(accWorkspace)"
                                class="d-flex align-center justify-center"
                            >
                                <v-icon color="secondary" class="icon" size="x-large">
                                    $apartment
                                </v-icon>
                            </v-col>
                            <v-col
                                :cols="$vuetify.display.mobile ? '10' : '8'"
                                @click="editAccountingWorkspace(accWorkspace)"
                            >
                                <div
                                    data-cy="accountingWorkspace-groupName"
                                    class="d-flex align-center primary--text"
                                >
                                    <span
                                        :class="$vuetify.display.mobile ? 'ml-2' : ''"
                                        style="font-weight: bold; font-size: 16pt"
                                        >{{ accWorkspace.name }}</span
                                    >
                                </div>
                                <div
                                    data-cy="accountingWorkspace-dossierName"
                                    v-if="
                                        accWorkspace.clientCompanies &&
                                        accWorkspace.clientCompanies.length
                                    "
                                >
                                    <v-icon size="x-small" class="mr-2">$fa-building</v-icon
                                    ><span style="font-weight: bold; font-size: 11pt"
                                        >Dossiers affectés : </span
                                    >{{ displayClientCompanies(accWorkspace) }}
                                </div>
                                <div v-if="accWorkspace.users && accWorkspace.users.length">
                                    <v-icon size="x-small" class="mr-2">$fa-user</v-icon
                                    ><span style="font-weight: bold; font-size: 11pt"
                                        >Utilisateurs affectés : </span
                                    >{{ displayWorkspaceUsers(accWorkspace) }}
                                </div>
                            </v-col>
                            <v-col cols="2" class="d-flex justify-space-around align-center">
                                <v-tooltip location="bottom">
                                    <template v-slot:activator="{ props }">
                                        <v-btn-icon
                                            v-bind="props"
                                            icon="$oss-edit"
                                            color="green"
                                            elevation="0"
                                            @click="editAccountingWorkspace(accWorkspace)"
                                            size="small"
                                        />
                                    </template>
                                    <span>Modifier</span>
                                </v-tooltip>
                                <v-tooltip location="bottom" v-if="enableWrite">
                                    <template v-slot:activator="{ props }">
                                        <v-btn-trash
                                            v-bind="props"
                                            @click="
                                                selectedAccountingWorkspace = accWorkspace;
                                                dialogDisconnectAccountingWorkspace = true;
                                            "
                                            size="small"
                                            color="new_red"
                                        />
                                    </template>
                                    <span>Supprimer</span>
                                </v-tooltip>
                            </v-col>
                        </v-row>
                    </v-card>
                </template>
            </v-virtual-scroll>
        </div>
        <v-row v-else class="d-flex justify-center">
            <v-col :cols="$vuetify.display.mobile ? '12' : '6'" class="my-0 pa-2">
                <v-card class="default_card ma-0" elevation="0" pa-0 style="margin: 5px">
                    <v-row class="d-flex align-center">
                        <v-col cols="2" v-if="!$vuetify.display.mobile">
                            <v-icon color="new_red" class="icon" size="x-large">
                                $apartment
                            </v-icon>
                        </v-col>
                        <v-col :cols="$vuetify.display.mobile ? '10' : '8'">
                            <div class="d-flex align-center primary--text">
                                <span
                                    :class="$vuetify.display.mobile ? 'ml-2' : ''"
                                    style="font-weight: bold; font-size: 16pt"
                                >
                                    Aucun espace de travail
                                </span>
                            </div>
                            <div class="d-flex align-center">
                                <span :class="$vuetify.display.mobile ? 'ml-2' : ''">
                                    Vous n'avez enregistré aucun espace de travail
                                </span>
                            </div>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>
        </v-row>
        <oss-dialog
            v-model="dialogCreateAccountingWorkspace"
            v-if="dialogCreateAccountingWorkspace"
            :title="'Créer un espace de travail'"
            :actions="actions"
            width="1000px"
        >
            <AccountingWorkspace
                :actions="actions"
                :accountingFirmId="accountingFirm.id"
                @accountingWorkspaceCreated="refreshAccountingWorkspaces"
            >
            </AccountingWorkspace>
        </oss-dialog>
        <oss-dialog
            v-model="dialogDisconnectAccountingWorkspace"
            :max-width="!$vuetify.display.mobile ? '650px' : undefined"
            :fullscreen="$vuetify.display.mobile"
            :hide-overlay="$vuetify.display.mobile"
            title="Supprimer l'espace de travail"
            valid-title="Retirer"
            @valid="deleteAccountingWorkspace(selectedAccountingWorkspace)"
        >
            <v-row class="mx-2">
                <v-col>
                    Vous êtes sur le point de supprimer le groupe de dossiers
                    <b>{{ selectedAccountingWorkspace.name }}</b
                    >. Confirmer la suppression ?
                </v-col>
            </v-row>
        </oss-dialog>
        <oss-dialog
            v-model="dialogEditAccountingWorkspace"
            v-if="dialogEditAccountingWorkspace"
            title="Edition de l'espace de travail"
            :actions="actions"
            width="1000px"
        >
            <AccountingWorkspace
                :actions="actions"
                :accountingFirmId="accountingFirm.id"
                :existingAccountingWorkspace="selectedAccountingWorkspace"
                @accountingWorkspaceUpdated="refreshAccountingWorkspaces"
            >
            </AccountingWorkspace>
        </oss-dialog>
    </v-container>
</template>

<script lang="ts">
import gql from "graphql-tag";
import _ from "lodash";
import { User } from "../../store/store";
import { Apollo } from "@/decorators";
import AccountingWorkspace from "../admin/AccountingWorkspace.vue";
import { DialogAction } from "../../services/dialogAction";
import getEnv from "../../env";
import DELETE_ACCOUNTING_WORKSPACE from "../../graphql/accountingWorkspace/DeleteAccountingWorkspace.gql";
import TutorialButtonDialog from "../tutorial/TutorialButtonDialog.vue";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";
import { checkAccessRights } from "../../services/accessRightServices";

const env = getEnv();

const BobbeeImg = require("../../assets/img/bobbee3d/bobbee3dTuto.png");

@Component({
    components: {
        AccountingWorkspace,
        TutorialButtonDialog,
    },
})
export class AccountingWorkspaces extends Vue {
    @Prop({ default: [] }) accountingFirm: any;

    newAccountingWorkspaceName: string = "";

    dialogCreateAccountingWorkspace: boolean = false;

    dialogDisconnectAccountingWorkspace: boolean = false;

    dialogEditAccountingWorkspace: boolean = false;

    newAccountingWorkspace: any = {
        id: "",
        name: "",
    };

    actions: any = {
        action: new DialogAction(),
    };

    selectedAccountingWorkspace: any = {
        id: "",
        name: "",
        clientCompanies: [],
        users: [],
    };

    search = "";

    get currentUser(): User {
        return this.$store.state.user;
    }

    get enableRead() {
        const authorized = checkAccessRights(this.currentUser, "accountingWorkspace", "read");
        return authorized;
    }

    get enableWrite() {
        const authorized = checkAccessRights(this.currentUser, "accountingWorkspace", "write");
        return authorized;
    }

    get sortedAccountingWorkspaces() {
        // Corriger ici / blinder car le ternaire n'est pas correct -> il faut que la company soit enableAccountingWorkspaces

        const accountingWorkspaces = this.accountingWorkspaces;
        let workspaces = accountingWorkspaces.sort((a: any, b: any) =>
            a.name.localeCompare(b.name),
        );
        if (this.search) {
            workspaces = workspaces.filter((x: { name: string }) =>
                x.name.toLowerCase().includes(this.search.toLowerCase()),
            );
        }
        return workspaces.sort((a: any, b: any) => a.name.localeCompare(b.name));
    }

    get maxHeight() {
        const innHeight = window.innerHeight;
        const vhHeight = (170 / innHeight) * 100;
        const maxHeight_vh = 100 - vhHeight;

        return maxHeight_vh + "vh";
    }

    initialize() {
        this.search = "";
        // this.selectedAccountingWorkspace = ""
    }

    beforeMount() {
        this.selectedAccountingWorkspace = {
            id: "",
            name: "",
            clientCompanies: [],
            users: [],
        };

        this.initialize();
    }

    refreshAccountingWorkspaces() {
        this.$apollo.queries.accountingWorkspaces.refetch();
        this.dialogCreateAccountingWorkspace = false;
        this.dialogEditAccountingWorkspace = false;
        this.dialogDisconnectAccountingWorkspace = false;
    }

    // clearAccountingWorkspace() {
    //     this.selectedAccountingWorkspace = {
    //         id: "",
    //         name: "",
    //         clientCompanies: [],
    //         users: []
    //     }
    // }

    @Apollo({
        query: gql`
            query companyDetails($id: ID!) {
                company(where: { id: $id }) {
                    id
                    accountingWorkspaces {
                        id
                        name
                        clientCompanies {
                            id
                            name
                        }
                        users {
                            id
                            firstname
                            lastname
                        }
                    }
                }
            }
        `,
        variables() {
            return {
                id: this.accountingFirm.id,
            };
        },
        skip() {
            return !this.enableRead || !this.accountingFirm?.id;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                debugger;
                if (data && data.company) {
                    if (data.company.accountingWorkspaces) {
                        this.accountingWorkspaces = data.company.accountingWorkspaces;
                    } else {
                        this.accountingWorkspaces = [];
                    }
                }
            }
        },
    })
    accountingWorkspaces: any = [];

    async deleteAccountingWorkspace(accountingWorkspace: any) {
        if (accountingWorkspace?.id) {
            const deletedWorkspaces = await this.$apollo.mutate({
                mutation: DELETE_ACCOUNTING_WORKSPACE,
                variables: {
                    id: accountingWorkspace.id,
                },
            });
            if (deletedWorkspaces?.data?.deleteAccountingWorkspace) {
                this.refreshAccountingWorkspaces();
                this.selectedAccountingWorkspace = {
                    id: "",
                    name: "",
                    clientCompanies: [],
                    users: [],
                };
            }
        }
    }

    editAccountingWorkspace(accountingWorkspace: any) {
        debugger;

        this.selectedAccountingWorkspace = {
            id: "",
            name: "",
            clientCompanies: [],
            users: [],
        };

        this.selectedAccountingWorkspace = accountingWorkspace;
        this.dialogEditAccountingWorkspace = true;
    }

    displayClientCompanies(accountingWorkspace) {
        if (accountingWorkspace?.clientCompanies?.length) {
            if (accountingWorkspace?.clientCompanies?.length <= 3) {
                return accountingWorkspace.clientCompanies
                    .map((clientCompany: any) => clientCompany.name)
                    .join(", ");
            } else {
                let string = "";
                for (let i = 0; i < 3; i++) {
                    string += accountingWorkspace.clientCompanies[i].name + (i < 2 ? ", " : " ");
                }
                string +=
                    "+ " +
                    (accountingWorkspace?.clientCompanies?.length - 3) +
                    " dossier(s) affecté(s)";
                return string;
            }
        } else {
            return "";
        }
    }

    displayWorkspaceUsers(accountingWorkspace) {
        if (accountingWorkspace?.users?.length) {
            if (accountingWorkspace?.users?.length <= 3) {
                return accountingWorkspace.users
                    .map((user: any) => user.firstname + " " + user.lastname)
                    .join(", ");
            } else {
                let string = "";
                for (let i = 0; i < 3; i++) {
                    string +=
                        accountingWorkspace.users[i].firstname +
                        " " +
                        accountingWorkspace.users[i].lastname +
                        (i < 2 ? ", " : " ");
                }
                string +=
                    "+ " + (accountingWorkspace?.users?.length - 3) + " utilisateur(s) affecté(s)";
                return string;
            }
        } else {
            return "";
        }
    }
}

export default toNative(AccountingWorkspaces);
</script>

<style scoped lang="scss">
.error,
.warning {
    color: #fff;
}
</style>
